import { useState } from "react";
import {
  interestAssistanceDocs,
  nodalOfficerConfigs,
  nodalOfficerDocumentTypeIdMap,
} from "../../../config/roleConfig";
import { applicationStatusMap } from "../../../config/applicationStatusConfig";
import { scriptRegex } from "../../../utils/utils";

const UpdateStatusCard = ({
  stage = "TIFC",
  onUpdate,
  pendingDocumentList = [],
  isInterestScheme = false,
}) => {
  const config = nodalOfficerConfigs[stage];
  const [status, setStatus] = useState("");
  const [remarks, setRemarks] = useState("");
  const [file, setFile] = useState(null);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [error, setError] = useState({ checkBoxList: "" });

  const nodalOfficerDocNames = new Set(
    Object.values(nodalOfficerDocumentTypeIdMap),
  );

  const interestAssistanceDocNames = new Set(
    Object.values(interestAssistanceDocs),
  );

  const docsList = pendingDocumentList.filter((doc) => {
    if (nodalOfficerDocNames.has(doc.name_en)) {
      return false;
    }

    if (!isInterestScheme && interestAssistanceDocNames.has(doc.name_en)) {
      return false;
    }

    if (isInterestScheme && !interestAssistanceDocNames.has(doc.name_en)) {
      return false;
    }

    return true;
  });

  // Check if status requires upload
  const requiresUpload =
    status &&
    config.requiresUpload(
      Object.values(applicationStatusMap).find((s) => s.value === status)
        ?.label,
    );

  const handleSubmit = () => {
    setError({ checkBoxList: "" });

    // Require at least one doc if "Document Pending"
    if (
      status === applicationStatusMap.incompleteDocumentPending.value &&
      selectedDocIds.length === 0
    ) {
      setError({
        checkBoxList: "Please select at least one pending document.",
      });
      return;
    }

    if (!status || !remarks) {
      alert("Status and remarks are required.");
      return;
    }
    if (requiresUpload && !file) {
      alert("Upload report is required for this status.");
      return;
    }

    onUpdate({
      status,
      remarks,
      reportFile: file,
      pendingDocs: selectedDocIds, // 👈 pass selected docs
    });
  };

  return (
    config.statuses && (
      <div className="grid grid-cols-1 gap-4 px-4 py-2">
        {/* Status dropdown */}
        <div>
          <label htmlFor="status" className="block font-medium text-gray-700">
            Application Status <span className="text-red-600">*</span>
          </label>
          <select
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="mt-1 block w-full border rounded px-3 py-2"
            required
          >
            <option value="" disabled>
              Select Status
            </option>
            {config.statuses.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {/* ✅ Pending Document Selection (only when status === Document Pending) */}
        {status === applicationStatusMap.incompleteDocumentPending.value && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Pending Documents
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {docsList?.map((item) => (
                <div key={item.id} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`doc-${item.id}`}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    checked={selectedDocIds.includes(item.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedDocIds((prev) => [...prev, item.id]);
                      } else {
                        setSelectedDocIds((prev) =>
                          prev.filter((id) => id !== item.id),
                        );
                      }
                    }}
                  />
                  <label
                    htmlFor={`doc-${item.id}`}
                    className="ml-2 text-sm text-gray-700 break-words"
                  >
                    {item.name_en}
                  </label>
                </div>
              ))}
            </div>
            {error.checkBoxList && (
              <p className="text-red-500 text-sm mt-2">{error.checkBoxList}</p>
            )}
          </div>
        )}

        {/* Remarks */}
        <div>
          <label htmlFor="remarks" className="block font-medium text-gray-700">
            Remarks <span className="text-red-600">*</span>
          </label>
          <textarea
            id="remarks"
            value={remarks}
            onChange={(e) => {
              if (!scriptRegex.test(e.target.value)) {
                setRemarks(e.target.value);
              }
            }}
            className="mt-1 block w-full border border-gray-300 rounded px-3 py-2 text-sm"
          ></textarea>
        </div>

        {/* File Upload (conditionally shown) */}
        {requiresUpload && (
          <div>
            <label className="block font-medium text-gray-700 mb-1">
              {typeof config.uploadLabel === "function"
                ? config.uploadLabel(
                    Object.values(applicationStatusMap).find(
                      (s) => s.value === status,
                    )?.value,
                  )
                : config.uploadLabel}
              <span className="text-red-600">*</span>
            </label>

            <div className="flex flex-col items-start">
              {/* Styled button */}
              <label
                htmlFor="upload-file"
                className="cursor-pointer inline-flex items-center justify-center px-5 py-2 bg-btn text-white text-sm font-medium rounded-md shadow hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-btn w-auto min-w-40"
              >
                📂 Choose File
              </label>
              <input
                id="upload-file"
                type="file"
                className="hidden"
                onChange={(e) => setFile(e.target.files[0])}
              />

              {/* Show chosen file name */}
              {file && (
                <p className="mt-2 text-sm text-gray-600 truncate">
                  {file.name}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Final Assign button */}
        <div className="flex justify-center">
          <button
            onClick={handleSubmit}
            disabled={!status || !remarks || (requiresUpload && !file)}
            className={
              !status || !remarks || (requiresUpload && !file)
                ? "px-5 py-2 rounded bg-gray-300 text-gray-600 cursor-not-allowed w-auto min-w-[160px]"
                : "bg-btn text-white px-5 py-2 rounded w-auto min-w-[160px] hover:opacity-90"
            }
          >
            Forward
          </button>
        </div>
      </div>
    )
  );
};

export default UpdateStatusCard;
